import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const Footer = () => {
    const { t, i18n } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);

    const toggleLanguage = () => {
        const newLang = i18n.language === 'en' ? 'ar' : 'en';
        i18n.changeLanguage(newLang);
        document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = newLang;
    };

    return (
        <div className={`fixed bottom-0 left-0 right-0 bg-gradient-to-b from-white to-gray-50 border-t border-gray-200 transition-transform duration-300 ${isVisible ? 'translate-y-0' : 'translate-y-full'}`}>
            {/* Toggle Button */}
            <button
                onClick={() => setIsVisible(!isVisible)}
                className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-t-lg px-4 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            >
                {isVisible ? 'Hide Footer' : 'Show Footer'}
            </button>

            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* About Section */}
                    <div className="col-span-1 md:col-span-2">
                        <h2 className="text-lg font-semibold text-gray-900 bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-transparent">
                            {t('site.title', 'MuslimTree')}
                        </h2>
                        <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                            {t('footer.description', 'Discover the rich history and knowledge of Islam through interactive visualizations and comprehensive data.')}
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase mb-2 text-green-700">
                            {t('footer.quickLinks', 'Quick Links')}
                        </h3>
                        <ul className="space-y-1">
                            <li>
                                <Link to="/about" className="text-sm text-gray-600 hover:text-green-600 transition-colors duration-200 flex items-center group">
                                    <span className="w-1 h-1 bg-green-500 rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                                    {t('footer.about', 'About Us')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/contact" className="text-sm text-gray-600 hover:text-green-600 transition-colors duration-200 flex items-center group">
                                    <span className="w-1 h-1 bg-green-500 rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                                    {t('footer.contact', 'Contact')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/privacy" className="text-sm text-gray-600 hover:text-green-600 transition-colors duration-200 flex items-center group">
                                    <span className="w-1 h-1 bg-green-500 rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                                    {t('footer.privacy', 'Privacy Policy')}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Language and Contact */}
                    <div>
                        <div className="mb-4">
                            <button
                                onClick={toggleLanguage}
                                className="text-sm text-gray-600 hover:text-green-600 transition-colors duration-200 flex items-center group"
                            >
                                <svg className="h-4 w-4 mr-1 text-green-500 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                </svg>
                                {i18n.language === 'en' ? 'العربية' : 'English'}
                            </button>
                        </div>

                        <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase mb-2 text-green-700">
                            {t('footer.connect', 'Connect')}
                        </h3>
                        <ul className="space-y-1">
                            <li>
                                <a href="mailto:contact@muslimtree.org" className="text-sm text-gray-600 hover:text-green-600 transition-colors duration-200 flex items-center group">
                                    <svg className="h-4 w-4 mr-1 text-green-500 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    contact@muslimtree.org
                                </a>
                            </li>
                            <li className="flex space-x-4 mt-2">
                                <a href="#" className="text-gray-400 hover:text-green-600 transition-colors duration-200 transform hover:scale-110">
                                    <span className="sr-only">Facebook</span>
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                                    </svg>
                                </a>
                                <a href="#" className="text-gray-400 hover:text-green-600 transition-colors duration-200 transform hover:scale-110">
                                    <span className="sr-only">Twitter</span>
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                                    </svg>
                                </a>
                                <a href="#" className="text-gray-400 hover:text-green-600 transition-colors duration-200 transform hover:scale-110">
                                    <span className="sr-only">Telegram</span>
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.11.02-1.93 1.23-5.46 3.62-.52.35-.99.53-1.41.52-.46-.01-1.35-.26-2.01-.48-.81-.27-1.45-.42-1.4-.89.03-.25.37-.51 1.03-.78 4.04-1.76 6.74-2.92 8.09-3.48 3.85-1.6 4.64-1.88 5.17-1.89.11 0 .37.03.54.17.14.12.18.28.2.45-.02.14-.02.3-.03.42z" />
                                    </svg>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Copyright */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-center text-xs text-gray-500">
                        {t('footer.copyright', '© {{year}} MuslimTree. All rights reserved.', { year: new Date().getFullYear() })}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Footer; 